import type { Node as ProseMirrorNode } from '@atlaskit/editor-prosemirror/model';
import type { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';
import type { SmartLinkResponse } from '@atlaskit/linking-types/smart-link';

const RESOLVE_TIMEOUT_MS = 15_000;
const PRELOAD_MAX_URLS = 20;
const PRELOAD_CONCURRENCY = 2;

type SmartLinkContext = ReturnType<typeof useSmartLinkContext>;

const inFlightByClient = new WeakMap<object, Map<string, Promise<SmartLinkResponse>>>();
export type SmartLinkResolutionError = 'transient' | 'unsupported';

const failedUrlsByClient = new WeakMap<object, Map<string, SmartLinkResolutionError>>();

export const getSmartLinkResolutionError = (error: unknown): SmartLinkResolutionError => {
	if (error instanceof Error && error.name === 'APIError') {
		const apiError = error as Error & { kind?: unknown; type?: unknown };
		if (
			apiError.kind === 'fatal' &&
			(apiError.type === 'ResolveUnsupportedError' || apiError.type === 'UnsupportedError')
		) {
			return 'unsupported';
		}
	}
	return 'transient';
};

export const isSmartLinkUnavailable = (context: SmartLinkContext, url: string): boolean => {
	const cached = context.store?.getState()[url];
	const inaccessible = Boolean(cached?.details && cached.details.meta?.access !== 'granted');
	if (cached?.status === 'resolved' && cached.details) {
		return inaccessible;
	}
	return (
		inaccessible ||
		getSmartLinkResolutionError(cached?.error) === 'unsupported' ||
		failedUrlsByClient.get(context.connections.client)?.get(url) === 'unsupported'
	);
};

export const hasFailedSmartLinkResolution = (context: SmartLinkContext, url: string): boolean => {
	const cached = context.store?.getState()[url];
	if (cached?.status === 'resolved' && cached.details) {
		return false;
	}
	return (
		cached?.status === 'errored' ||
		cached?.status === 'fallback' ||
		failedUrlsByClient.get(context.connections.client)?.has(url) === true
	);
};

export const resolveSmartLinkForAI = (
	context: SmartLinkContext,
	url: string,
): Promise<SmartLinkResponse> => {
	const { store, connections } = context;
	const client = connections.client;
	const cached = store?.getState()[url];
	if (cached?.status === 'resolved' && cached.details) {
		failedUrlsByClient.get(client)?.delete(url);
		return Promise.resolve(cached.details);
	}
	const hydrateStore = (response: SmartLinkResponse): SmartLinkResponse => {
		const current = store?.getState()[url];
		if (current?.status !== 'resolved' || current.details !== response) {
			store?.dispatch({ type: 'resolved', url, payload: response });
		}
		return response;
	};

	let clientRequests = inFlightByClient.get(client);
	if (!clientRequests) {
		clientRequests = new Map();
		inFlightByClient.set(client, clientRequests);
	}

	const existingRequest = clientRequests.get(url);
	if (existingRequest) {
		return existingRequest.then(hydrateStore);
	}

	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(
			() => reject(new Error('Smart Link resolution timed out')),
			RESOLVE_TIMEOUT_MS,
		);
	});
	const request = Promise.race([Promise.resolve().then(() => client.fetchData(url)), timeout])
		.then(
			(response) => {
				failedUrlsByClient.get(client)?.delete(url);
				return response;
			},
			(error: unknown) => {
				let failedUrls = failedUrlsByClient.get(client);
				if (!failedUrls) {
					failedUrls = new Map();
					failedUrlsByClient.set(client, failedUrls);
				}
				failedUrls.set(url, getSmartLinkResolutionError(error));
				throw error;
			},
		)
		.finally(() => clearTimeout(timeoutId));

	clientRequests.set(url, request);
	const clearInFlight = () => {
		if (clientRequests?.get(url) === request) {
			clientRequests.delete(url);
		}
	};
	void request.then(clearInFlight, clearInFlight);
	return request.then(hydrateStore);
};

const collectSmartLinkUrls = (doc: ProseMirrorNode): string[] => {
	const urls = new Set<string>();
	doc.descendants((node) => {
		if (urls.size >= PRELOAD_MAX_URLS) {
			return false;
		}
		for (const mark of node.marks) {
			if (mark.type.name === 'link' && typeof mark.attrs.href === 'string') {
				urls.add(mark.attrs.href);
			}
		}
		if (
			['inlineCard', 'blockCard', 'embedCard'].includes(node.type.name) &&
			typeof node.attrs.url === 'string'
		) {
			urls.add(node.attrs.url);
		}
	});
	return [...urls].slice(0, PRELOAD_MAX_URLS);
};

export const preloadSmartLinksForAI = async (
	context: SmartLinkContext,
	doc: ProseMirrorNode,
	signal: AbortSignal,
): Promise<void> => {
	const urls = collectSmartLinkUrls(doc);
	let nextIndex = 0;
	const worker = async () => {
		while (nextIndex < urls.length && !signal.aborted) {
			const url = urls[nextIndex++];
			try {
				await resolveSmartLinkForAI(context, url);
			} catch {
				// Preloading is best effort; activation can retry a failed request.
			}
		}
	};
	await Promise.all(Array.from({ length: PRELOAD_CONCURRENCY }, worker));
};
